/**
 * useTenantSelf — the caller's firm name + pricing-tier label for the org
 * header (Story UI-U2 AC 5). Also reusable by UI-U6's admin shell.
 *
 * live mode → GET /tenant/self via the typed client; any error → null result
 * (brand fallback "Brokly", no tier) — the shell NEVER blocks on this request
 * and never retry-storms.
 * mock mode → fixture matching the seeded demo tenant.
 *
 * [Source: apps/web/src/features/dashboard/hooks/useHeuteRenewals.ts] (mock/live pattern)
 * [Source: docs/stories/ui-u2-ia-navigation.md AC 5]
 */

import { useQuery } from '@tanstack/react-query';
import type { TenantSelfResponse } from '@brokly/shared';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient } from '../../lib/api-client';

/** Mock-mode fixture — matches the seeded demo tenant (packages/db seed). */
export const TENANT_SELF_FIXTURE: TenantSelfResponse = {
  id: 'd0000000-0000-4000-8000-000000000001',
  name: 'Demo Brokerfirma',
  pricing_tier_label: 'Enterprise',
};

/** null = not (yet) available → header renders the brand fallback. */
export function useTenantSelf(): TenantSelfResponse | null {
  const live = isLiveData();
  const { data } = useQuery<TenantSelfResponse | null>({
    queryKey: qk.tenantSelf(),
    queryFn: async () => {
      if (!live) return TENANT_SELF_FIXTURE;
      try {
        return await apiClient.tenant.self();
      } catch {
        // Deliberate degradation (AC 5): display-only data — a failed read
        // renders the brand fallback instead of surfacing an error.
        return null;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    ...(live ? {} : { initialData: TENANT_SELF_FIXTURE }),
  });
  return data ?? null;
}
